import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";

import { getStorefrontDeliveryStatus } from "../services/businessNotificationService";

/**
 * Tells the store that customers cannot order from it yet, and why.
 *
 * Every order is delivered, so a store without a confirmed dispatch location and
 * active delivery pricing is visible to shoppers but cannot be bought from. The
 * shopper is told so on the product page; without this the store would only
 * notice that no orders were coming in.
 *
 * Silent while loading, on error, and when the store is ready: it is a warning,
 * and a warning that can be wrong is worse than none.
 */
export function missingSetupSteps(status) {
  const steps = [];
  if (!status?.originConfirmed) steps.push("Confirm your dispatch location on the map");
  if (!status?.feePolicyActive) steps.push("Save and activate your delivery pricing");
  return steps;
}

export default function StorefrontDeliveryWarning() {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getStorefrontDeliveryStatus()
      .then((data) => { if (!cancelled) setStatus(data || null); })
      .catch(() => { if (!cancelled) setStatus(null); });
    return () => { cancelled = true; };
  }, []);

  if (status?.acceptsDeliveryOrders !== false) return null;
  const steps = missingSetupSteps(status);

  return (
    <section
      role="alert"
      className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <h2 className="font-semibold">Customers can&apos;t order from your store yet.</h2>
          <p className="mt-1 leading-relaxed text-amber-800 dark:text-amber-200">
            Your products are visible, but customers cannot order until a confirmed dispatch
            location is set up and delivery pricing is active.
          </p>
          {steps.length > 0 && (
            <ul className="mt-2 list-disc space-y-0.5 pl-5">
              {steps.map((step) => <li key={step}>{step}</li>)}
            </ul>
          )}
          {status.canManage ? (
            <Link
              to="/business/settings?tab=delivery"
              className="mt-3 inline-flex rounded-xl bg-amber-900 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-800 dark:bg-amber-200 dark:text-amber-950 dark:hover:bg-amber-100"
            >
              Finish delivery setup
            </Link>
          ) : (
            <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
              Ask your store owner to finish delivery setup.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
